import React, { useState, useEffect } from "react";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * Statistics dashboard showing production and pending message inventory.
 */
const GeneratorStats = ({ onSwitchToApproval }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const data = await SimulationDataGeneratorAPI.getStats();
        setStats(data);
      } catch (err) {
        console.error("Failed to fetch stats:", err);
        setError("Failed to load statistics");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <LoadingSpinner message="Loading statistics..." />;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!stats) {
    return (
      <div className="empty-state">
        <p>No statistics available.</p>
      </div>
    );
  }

  const maxLevelCount = stats.bySophisticationLevel
    ? Math.max(...Object.values(stats.bySophisticationLevel), 1)
    : 1;

  const maxContextCount = stats.byContext
    ? Math.max(...Object.values(stats.byContext), 1)
    : 1;

  return (
    <div className="generator-stats">
      {/* ── Production Messages ── */}
      <div className="stats-section">
        <h3>Production Messages</h3>
        <div className="stat-cards">
          <div className="stat-card">
            <div className="stat-value">{stats.totalMessages}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.emailCount}</div>
            <div className="stat-label">Email</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.smsCount}</div>
            <div className="stat-label">SMS</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.voiceCount}</div>
            <div className="stat-label">Voice</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.socialMediaCount}</div>
            <div className="stat-label">Social Media</div>
          </div>
        </div>

        {/* By Sophistication Level */}
        {stats.bySophisticationLevel &&
          Object.keys(stats.bySophisticationLevel).length > 0 && (
            <>
              <h4
                style={{
                  fontSize: "0.85rem",
                  color: "#64748b",
                  margin: "12px 0 8px",
                }}
              >
                By Sophistication Level
              </h4>
              <div className="stat-bar-chart">
                {[...Array(10)].map((_, i) => {
                  const level = i + 1;
                  const count = stats.bySophisticationLevel[level] || 0;
                  return (
                    <div key={level} className="stat-bar-row">
                      <span className="stat-bar-label">Level {level}</span>
                      <div className="stat-bar-track">
                        <div
                          className="stat-bar-fill"
                          style={{
                            width: `${(count / maxLevelCount) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="stat-bar-value">{count}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
      </div>

      {/* ── Pending Messages + Context Breakdown ── */}
      <div className="stats-section">
        <h3>Pending Messages</h3>
        <div className="stat-cards">
          <div className="stat-card">
            <div className="stat-value">{stats.pendingCount}</div>
            <div className="stat-label">Total Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.pendingEmailCount}</div>
            <div className="stat-label">Email Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.pendingSmsCount}</div>
            <div className="stat-label">SMS Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.pendingVoiceCount}</div>
            <div className="stat-label">Voice Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.pendingSocialMediaCount}</div>
            <div className="stat-label">Social Media Pending</div>
          </div>
        </div>

        {stats.pendingCount > 0 && (
          <div className="pending-cta">
            <span>
              You have {stats.pendingCount} messages awaiting approval
            </span>
            <button onClick={onSwitchToApproval}>Review Now</button>
          </div>
        )}

        {/* By Context */}
        {stats.byContext && Object.keys(stats.byContext).length > 0 && (
          <>
            <h4
              style={{
                fontSize: "0.85rem",
                color: "#64748b",
                margin: "16px 0 8px",
              }}
            >
              Production Messages by Context
            </h4>
            <div className="stat-bar-chart">
              {Object.entries(stats.byContext)
                .sort(([, a], [, b]) => b - a)
                .map(([ctx, count]) => (
                  <div key={ctx} className="stat-bar-row">
                    <span className="stat-bar-label">{ctx}</span>
                    <div className="stat-bar-track">
                      <div
                        className="stat-bar-fill"
                        style={{
                          width: `${(count / maxContextCount) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="stat-bar-value">{count}</span>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GeneratorStats;
